import Box from "@memorang/ui/components/Box";
import SheetHandle from "@memorang/ui/components/SheetHandle";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import type { FeedbackParams } from "../../feedback/types";
import { SummaryActions } from "../../qa-search/SummaryActions";
import type { HandleTracking } from "../../types";
import { useAppTheme } from "../../useAppTheme";
import type { SessionMetadata as SessionMetadataType } from "../types";

type Props = {
	handleExpandCollapse: () => void;
	sessionAnalysisData: string;
	handleTracking: HandleTracking;
	feedbackDialogParams?: FeedbackParams;
	sessionMetadata?: SessionMetadataType;
};

const SessionMetadata = ({
	sessionMetadata,
}: { sessionMetadata?: SessionMetadataType }) => {
	const { t } = useTranslation();

	if (!sessionMetadata) {
		return null;
	}

	const { sessionDate, sessionTitle, numItems } = sessionMetadata;
	const caption = `${numItems} ${t("common.item", { count: numItems })}, ${sessionDate}`;

	return (
		<Box width="100%" flexDirection="row" alignItems="center" gap={8}>
			<Text
				variant="bodySmall"
				numberOfLines={2}
				style={{
					fontStyle: "italic",
				}}
			>
				{sessionTitle ? `${sessionTitle} ● ${caption}` : caption}
			</Text>
		</Box>
	);
};

export const FullReportHandle = ({
	handleExpandCollapse,
	sessionAnalysisData,
	handleTracking,
	feedbackDialogParams,
	sessionMetadata,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	return (
		<Pressable onPress={handleExpandCollapse}>
			<Box
				width="100%"
				style={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
			>
				<SheetHandle
					onPress={handleExpandCollapse}
					backgroundColor={theme.colors.border}
				/>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					paddingHorizontal={16}
					paddingBottom={20}
					paddingTop={8}
					gap={8}
				>
					<Icon name="brain" size={24} color={theme.colors.primary} />
					<Box flex={1}>
						<Text variant="titleLarge">
							{t("report.sessionAnalysis.title")}
						</Text>
						<SessionMetadata sessionMetadata={sessionMetadata} />
					</Box>
					<SummaryActions
						showEmail
						text={sessionAnalysisData}
						handleTracking={handleTracking}
						feedbackDialogParams={feedbackDialogParams}
					/>
				</Box>
			</Box>
		</Pressable>
	);
};
